/**
 * App Component
 * Main application layout with tabs and filters
 */

import { useState, useMemo, useCallback } from 'react'
import { Upload, BarChart3, Settings, Tv, RefreshCw, Trash2, Cloud, Plus, Search, X, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react'
import { useAnimeData } from './hooks/useAnimeData'
import { ErrorBoundary } from './components/ErrorBoundary'
import { AnimeCard } from './components/AnimeCard'
import { AnimeDetailModal } from './components/AnimeDetailModal'
import { ImportModal } from './components/ImportModal'
import { AddAnimeModal } from './components/AddAnimeModal'
import { DropReasonModal } from './components/DropReasonModal'
import { AnalysisView } from './components/AnalysisView'

// プラットフォームフィルターオプション
const PLATFORM_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'netflix', label: 'Netflix' },
  { id: 'prime', label: 'Prime' },
  { id: 'unext', label: 'U-NEXT' }
]

// タブ定義
const TABS = [
  { id: 'watching', label: '視聴中', icon: Tv },
  { id: 'waiting', label: '未視聴', icon: null },
  { id: 'completed', label: '完走', icon: null },
  { id: 'analysis', label: '分析', icon: BarChart3 },
  { id: 'void', label: 'ゴミ箱', icon: Trash2 }
]

function AppContent() {
  const {
    animeList,
    watchingList,
    planToWatchList,
    completedList,
    droppedList,
    loading,
    useApi,
    importRawData,
    updateAnime,
    incrementEpisode,
    dropAnime,
    skipAnime,
    deleteAnime,
    addAnime,
    syncMetadata,
    migrateToCloud,
    exportData,
    importJsonData
  } = useAnimeData()

  // UI State
  const [activeTab, setActiveTab] = useState('watching')
  const [platformFilter, setPlatformFilter] = useState('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [searchExpanded, setSearchExpanded] = useState(false)
  const [sortBy, setSortBy] = useState('updated') // updated, score, title, broadcast
  const [sortOrder, setSortOrder] = useState('desc') // asc, desc
  const [importModalOpen, setImportModalOpen] = useState(false)
  const [detailModalOpen, setDetailModalOpen] = useState(false)
  const [selectedAnime, setSelectedAnime] = useState(null)
  const [dropModalOpen, setDropModalOpen] = useState(false)
  const [dropTarget, setDropTarget] = useState(null)
  const [dropActionType, setDropActionType] = useState('drop')
  const [isSyncing, setIsSyncing] = useState(false)
  const [isMigrating, setIsMigrating] = useState(false)
  const [addModalOpen, setAddModalOpen] = useState(false)

  // メタデータ同期ハンドラ
  const handleSync = async () => {
    if (isSyncing) return
    setIsSyncing(true)
    try {
      await syncMetadata()
    } finally {
      setIsSyncing(false)
    }
  }

  // クラウド移行ハンドラ
  const handleMigrateToCloud = async () => {
    if (isMigrating) return
    if (!window.confirm('ローカルデータをクラウドへ移行しますか？')) return

    setIsMigrating(true)
    try {
      const result = await migrateToCloud()
      alert(`移行完了: ${result.migrated}件のデータをクラウドへ移行しました`)
      window.location.reload()
    } catch (err) {
      alert(`移行エラー: ${err.message}`)
    } finally {
      setIsMigrating(false)
    }
  }

  // データをクリップボードにコピー
  const handleCopyData = async () => {
    const data = exportData()
    await navigator.clipboard.writeText(data)
    alert(`${animeList.length}件のデータをクリップボードにコピーしました。\n本番サイトでImport→JSONタブに貼り付けてください。`)
  }

  // 現在のタブに応じたリストを取得
  const currentList = useMemo(() => {
    switch (activeTab) {
      case 'watching':
        return watchingList
      case 'void':
        return droppedList
      case 'waiting':
        return planToWatchList
      case 'completed':
        return completedList
      case 'analysis':
        return animeList
      default:
        return []
    }
  }, [activeTab, watchingList, droppedList, planToWatchList, completedList, animeList])

  // プラットフォーム + タイトル検索 + ソートでフィルタリング
  const filteredList = useMemo(() => {
    let result = [...currentList]

    // プラットフォームフィルター
    if (platformFilter !== 'all') {
      result = result.filter(anime => anime.platform === platformFilter)
    }

    // タイトル検索フィルター
    if (searchQuery.trim()) {
      const query = searchQuery.trim().toLowerCase()
      result = result.filter(anime =>
        anime.title?.toLowerCase().includes(query)
      )
    }

    // ソート処理
    result.sort((a, b) => {
      let comparison = 0
      let invertForDesc = true // 通常はdescで反転

      switch (sortBy) {
        case 'score':
          comparison = (a.daveScore || 0) - (b.daveScore || 0)
          break
        case 'title':
          // タイトル：↓であ→わ、↑でわ→あ（自然な感覚）
          comparison = (a.title || '').localeCompare(b.title || '', 'ja')
          invertForDesc = false // タイトルは反転しない
          break
        case 'broadcast': {
          // 更新順：broadcastDayを基に直近で更新されたものを上位
          const dayMap = { 'Sundays': 0, 'Mondays': 1, 'Tuesdays': 2, 'Wednesdays': 3, 'Thursdays': 4, 'Fridays': 5, 'Saturdays': 6 }
          const today = new Date().getDay()

          const getDaysAgo = (broadcastDay) => {
            if (!broadcastDay) return 999 // 曜日なしは最後
            const dayNum = dayMap[broadcastDay]
            if (dayNum === undefined) return 999
            // 直近何日前に更新されたか（0-6、7日以内）
            let diff = today - dayNum
            if (diff < 0) diff += 7
            return diff <= 3 ? diff : 999 // 3日以内のみ優先
          }

          const aDays = getDaysAgo(a.broadcastDay)
          const bDays = getDaysAgo(b.broadcastDay)
          comparison = aDays - bDays
          break
        }
        case 'updated':
        default:
          comparison = new Date(b.lastUpdated || 0) - new Date(a.lastUpdated || 0)
          break
      }

      if (invertForDesc) {
        return sortOrder === 'asc' ? comparison : -comparison
      } else {
        // タイトル用：descであ→わ
        return sortOrder === 'desc' ? comparison : -comparison
      }
    })

    return result
  }, [currentList, platformFilter, searchQuery, sortBy, sortOrder])

  // カードをクリックして詳細モーダルを開く
  const handleCardClick = useCallback((anime) => {
    setSelectedAnime(anime)
    setDetailModalOpen(true)
  }, [])

  // 詳細モーダルで保存
  const handleDetailSave = useCallback((updatedAnime) => {
    updateAnime(updatedAnime.id, updatedAnime)
  }, [updateAnime])

  // ドロップボタンクリック
  const handleDropClick = useCallback((animeId) => {
    const anime = animeList.find(a => a.id === animeId)
    if (anime) {
      setDropTarget(anime)
      setDropActionType('drop')
      setDropModalOpen(true)
    }
  }, [animeList])

  // スキップボタンクリック
  const handleSkipClick = useCallback((animeId) => {
    const anime = animeList.find(a => a.id === animeId)
    if (anime) {
      setDropTarget(anime)
      setDropActionType('skip')
      setDropModalOpen(true)
    }
  }, [animeList])

  // 長押しでゴミ箱へ移動（確認済み）
  const handleLongPress = useCallback((animeId) => {
    dropAnime(animeId, '長押しで削除')
  }, [dropAnime])

  // ドロップ/スキップ確定
  const handleDropConfirm = useCallback((reason) => {
    if (!dropTarget) return

    if (dropActionType === 'drop') {
      dropAnime(dropTarget.id, reason)
    } else {
      skipAnime(dropTarget.id, reason)
    }
    setDropTarget(null)
  }, [dropTarget, dropActionType, dropAnime, skipAnime])

  // ローディング表示
  if (loading) {
    return (
      <div className="app">
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh'
        }}>
          <div className="text-gradient" style={{ fontSize: '1.25rem' }}>
            Loading...
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="app">
      {/* Header */}
      <header className="header">
        <div className="header-content">
          <h1 className="logo">エンタメ台帳</h1>
          <div style={{ display: 'flex', gap: '8px' }}>
            {useApi ? (
              /* API接続時：追加ボタンのみ表示 */
              <button
                className="btn btn-primary"
                onClick={() => setAddModalOpen(true)}
                title="新規アニメを追加"
              >
                <Plus size={16} />
                追加
              </button>
            ) : (
              /* ローカルモード：Import/クラウド移行ボタン */
              <>
                <button
                  className="btn btn-secondary"
                  onClick={() => setImportModalOpen(true)}
                >
                  <Upload size={16} />
                  Import
                </button>
                {animeList.length > 0 && (
                  <button
                    className="btn btn-primary"
                    onClick={handleMigrateToCloud}
                    disabled={isMigrating}
                    title="ローカルデータをクラウドへ移行"
                  >
                    <Cloud size={16} className={isMigrating ? 'spin' : ''} />
                    {isMigrating ? '移行中...' : 'クラウドへ'}
                  </button>
                )}
              </>
            )}
            <button
              className="btn btn-secondary"
              onClick={handleSync}
              disabled={isSyncing}
              title="画像の再取得・更新"
            >
              <RefreshCw size={16} className={isSyncing ? 'spin' : ''} />
              {isSyncing ? 'Syncing...' : 'Sync'}
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="main-content">
        {/* Tabs */}
        <div className="tabs">
          {TABS.map(tab => (
            <button
              key={tab.id}
              className={`tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.icon && <tab.icon size={14} style={{ marginRight: '4px' }} />}
              {tab.label}
            </button>
          ))}
        </div>

        {/* Analysis タブの場合は専用ビューを表示 */}
        {activeTab === 'analysis' ? (
          <AnalysisView animeList={animeList} />
        ) : (
          <>
            {/* Platform Filter Pills + Search */}
            <div className="filter-bar">
              {PLATFORM_FILTERS.map(filter => (
                <button
                  key={filter.id}
                  className={`filter-pill ${platformFilter === filter.id ? 'active' : ''}`}
                  data-platform={filter.id}
                  onClick={() => setPlatformFilter(filter.id)}
                >
                  {filter.label}
                </button>
              ))}

              {/* ソートドロップダウン */}
              <div className="sort-container">
                <select
                  className="sort-select"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  <option value="updated">更新日順</option>
                  <option value="broadcast">更新順</option>
                  <option value="score">スコア順</option>
                  <option value="title">タイトル順</option>
                </select>
                <button
                  className="sort-order-btn"
                  onClick={() => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
                  title={sortOrder === 'asc' ? '昇順' : '降順'}
                >
                  {sortOrder === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />}
                </button>
              </div>

              {/* 検索バー（折りたたみ式） */}
              <div className="search-bar-container">
                {searchExpanded ? (
                  <div className="search-bar">
                    <Search size={14} className="search-icon" />
                    <input
                      type="text"
                      className="search-input"
                      placeholder="タイトル検索..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      autoFocus
                    />
                    <button
                      className="search-close"
                      onClick={() => {
                        setSearchExpanded(false)
                        setSearchQuery('')
                      }}
                    >
                      <X size={14} />
                    </button>
                  </div>
                ) : (
                  <button
                    className="filter-pill search-toggle"
                    onClick={() => setSearchExpanded(true)}
                  >
                    <Search size={14} />
                  </button>
                )}
              </div>
            </div>

            {/* Cards Grid */}
            {filteredList.length > 0 ? (
              <div className="cards-grid">
                {filteredList.map(anime => (
                  <AnimeCard
                    key={anime.id}
                    anime={anime}
                    onIncrement={incrementEpisode}
                    onEdit={handleCardClick}
                    onDrop={handleDropClick}
                    onSkip={handleSkipClick}
                    onLongPress={handleLongPress}
                  />
                ))}
              </div>
            ) : (
              <div className="empty-state">
                <div className="empty-state-icon">{searchQuery ? '🔍' : '📺'}</div>
                <p className="empty-state-title">
                  {searchQuery ? '見つかりませんでした' :
                   activeTab === 'void' ? 'ドロップした作品はありません' :
                   activeTab === 'waiting' ? '視聴予定の作品はありません' :
                   activeTab === 'completed' ? '完走した作品はありません' :
                   '視聴中の作品はありません'}
                </p>
                <p>
                  {searchQuery ? (
                    <span style={{ fontSize: '0.85rem', opacity: 0.6 }}>
                      「{searchQuery}」に一致する作品がありません
                    </span>
                  ) : activeTab === 'watching' && (
                    <button
                      className="btn btn-primary"
                      onClick={() => setImportModalOpen(true)}
                      style={{ marginTop: '16px' }}
                    >
                      <Upload size={16} />
                      データをインポート
                    </button>
                  )}
                </p>
              </div>
            )}
          </>
        )}
      </main>

      {/* Modals */}
      <ImportModal
        isOpen={importModalOpen}
        onClose={() => setImportModalOpen(false)}
        onImport={importRawData}
        onImportJson={importJsonData}
      />

      <AnimeDetailModal
        anime={selectedAnime}
        isOpen={detailModalOpen}
        onClose={() => {
          setDetailModalOpen(false)
          setSelectedAnime(null)
        }}
        onSave={handleDetailSave}
        onDelete={deleteAnime}
      />

      <DropReasonModal
        isOpen={dropModalOpen}
        onClose={() => {
          setDropModalOpen(false)
          setDropTarget(null)
        }}
        onConfirm={handleDropConfirm}
        title={dropTarget?.title || ''}
        actionType={dropActionType}
      />

      <AddAnimeModal
        isOpen={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onAdd={addAnime}
        onAdded={() => setActiveTab('waiting')}
      />
    </div>
  )
}

// Error Boundaryでラップ
export default function App() {
  return (
    <ErrorBoundary>
      <AppContent />
    </ErrorBoundary>
  )
}
